import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHandshake,
  faSort,
  faSortUp,
  faSortDown,
  faSearch,
  faInfoCircle,
  faChevronLeft,
  faChevronRight,
  faPause,
  faPlay,
  faBan,
  faRotateLeft,
  faCopy,
} from "@fortawesome/free-solid-svg-icons";
import { Tooltip } from "@mui/material";
import ActionButton from "../ui/ActionButton";
import FormattedDate from "../ui/FormattedDate";
import LastUpdatedBadge from "../ui/LastUpdatedBadge";
import LoadingSpinner from "../ui/LoadingSpinner";
import { useDataCache, CACHE_KEYS } from "../../contexts/DataCacheContext";
import { useAuth } from "../../contexts/AuthContext";
import { useDebounce } from "../../hooks/useDebounce";
import PartnershipActionModal from "../Modals/PartnershipActionModal";
import {
  getOrgPartnerships,
  pausePartnership,
  resumePartnership,
  revokePartnership,
  restorePartnership,
} from "../../api/partnershipsApi";

const STATUS_COLORS = {
  pending:  { bg: "rgba(255,167,38,0.12)", color: "#ffa726" },
  active:   { bg: "rgba(102,187,106,0.12)", color: "#66bb6a" },
  paused:   { bg: "rgba(158,158,158,0.12)", color: "#9e9e9e" },
  revoked:  { bg: "rgba(239,83,80,0.12)",  color: "#ef5350" },
};

const StatusBadge = ({ status }) => {
  const s = STATUS_COLORS[status] || STATUS_COLORS.pending;
  return (
    <span
      className="admin-status-badge"
      style={{ backgroundColor: s.bg, color: s.color }}
    >
      {status}
    </span>
  );
};

const PAGE_SIZE = 10;

function PartnershipsSection({ darkMode, t }) {
  const { user } = useAuth();
  const [partnerships, setPartnerships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  // filters
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearch = useDebounce(searchQuery, 300);
  const [filterStatus, setFilterStatus] = useState("all");
  const [sortConfig, setSortConfig] = useState({ key: "created_at", direction: "desc" });
  const [page, setPage] = useState(1);

  // action modal
  const [actionModal, setActionModal] = useState({ open: false, action: null, id: null, partnerName: "" });
  const [actionProcessing, setActionProcessing] = useState(false);

  const sectionTopRef = useRef(null);
  const { getCachedData, setCachedData } = useDataCache();

  const orgId = user?.organization_id;

  const fetchData = useCallback(async (useCache = true) => {
    if (!orgId) return;

    try {
      if (useCache) {
        const cached = getCachedData(CACHE_KEYS.ADMIN_PARTNERSHIPS);
        if (cached?.data) {
          setPartnerships(cached.data);
          setLastUpdated(cached.timestamp);
          setLoading(false);
          return;
        }
      }

      setRefreshing(true);
      const res = await getOrgPartnerships(orgId);
      const data = res?.partnerships || [];
      const timestamp = Date.now();

      setPartnerships(data);
      setLastUpdated(timestamp);
      setCachedData(CACHE_KEYS.ADMIN_PARTNERSHIPS, data, timestamp);
      setError(null);
    } catch (err) {
      console.error("Error fetching org partnerships:", err);
      setError(err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [orgId, getCachedData, setCachedData]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleRefresh = useCallback(() => { fetchData(false); }, [fetchData]);

  const openActionModal = useCallback((id, action, partnerName = "") => {
    setActionModal({ open: true, action, id, partnerName });
  }, []);

  const handleActionConfirm = useCallback(async (notes) => {
    const { id, action } = actionModal;
    setActionProcessing(true);
    try {
      if (action === "pause") await pausePartnership(id, notes);
      else if (action === "resume") await resumePartnership(id, notes);
      else if (action === "revoke") await revokePartnership(id, notes);
      else if (action === "restore") await restorePartnership(id, notes);
      setActionModal({ open: false, action: null, id: null, partnerName: "" });
      fetchData(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setActionProcessing(false);
    }
  }, [actionModal, fetchData]);

  const handleCopyKey = useCallback((key) => {
    navigator.clipboard.writeText(key);
  }, []);

  // filtering + sorting
  const filteredRows = useMemo(() => {
    let rows = [...partnerships];

    // status filter
    if (filterStatus !== "all") {
      rows = rows.filter((p) => p.status === filterStatus);
    }

    // search
    if (debouncedSearch) {
      const q = debouncedSearch.toLowerCase();
      rows = rows.filter((p) =>
        (p.donor_username || "").toLowerCase().includes(q) ||
        (p.receiver_username || "").toLowerCase().includes(q) ||
        (p.donor_org_name || "").toLowerCase().includes(q) ||
        (p.receiver_org_name || "").toLowerCase().includes(q) ||
        (p.partner_key || "").toLowerCase().includes(q)
      );
    }

    // sort
    const { key, direction } = sortConfig;
    rows.sort((a, b) => {
      let aVal = a[key], bVal = b[key];

      if (key === "created_at" || key === "claimed_at") {
        aVal = aVal ? new Date(aVal).getTime() : 0;
        bVal = bVal ? new Date(bVal).getTime() : 0;
      } else if (key === "max_listing_pct") {
        aVal = aVal ?? 0;
        bVal = bVal ?? 0;
      } else {
        aVal = (aVal || "").toString().toLowerCase();
        bVal = (bVal || "").toString().toLowerCase();
      }

      if (aVal < bVal) return direction === "asc" ? -1 : 1;
      if (aVal > bVal) return direction === "asc" ? 1 : -1;
      return 0;
    });

    return rows;
  }, [partnerships, filterStatus, debouncedSearch, sortConfig]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const pagedRows = filteredRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // reset page when filters change
  useEffect(() => { setPage(1); }, [filterStatus, debouncedSearch]);

  const handleSort = (key) => {
    setSortConfig((prev) =>
      prev.key === key
        ? { key, direction: prev.direction === "asc" ? "desc" : "asc" }
        : { key, direction: "asc" }
    );
  };

  const SortIcon = ({ col }) => {
    if (sortConfig.key !== col) return <FontAwesomeIcon icon={faSort} className="sort-icon" />;
    return <FontAwesomeIcon icon={sortConfig.direction === "asc" ? faSortUp : faSortDown} className="sort-icon active" />;
  };

  // KPIs
  const kpis = useMemo(() => {
    const total = partnerships.length;
    const active = partnerships.filter((p) => p.status === "active").length;
    const paused = partnerships.filter((p) => p.status === "paused").length;
    const revoked = partnerships.filter((p) => p.status === "revoked").length;
    const pending = partnerships.filter((p) => p.status === "pending").length;
    const uniqueOrgs = new Set([
      ...partnerships.map((p) => p.donor_org_id),
      ...partnerships.map((p) => p.receiver_org_id),
    ].filter(Boolean)).size;
    return { total, active, paused, revoked, pending, uniqueOrgs };
  }, [partnerships]);

  if (loading) {
    return (
      <div className="profile-section admin-section-wide">
        <div className="admin-loading">
          <LoadingSpinner size={40} />
          <span>{t("Loading partnerships...")}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-section admin-section-wide" ref={sectionTopRef}>
      <div className="profile-section-header">
        <div className="profile-section-icon admin-icon">
          <FontAwesomeIcon icon={faHandshake} />
        </div>
        <div style={{ flex: 1 }}>
          <h2 className="profile-section-title">{t("Partnerships")}</h2>
          <p className="profile-section-desc">
            {t("Manage and monitor all partnerships across your organization")}
          </p>
        </div>
        <div className="header-status-area">
          <LastUpdatedBadge
            timestamp={lastUpdated}
            onRefresh={handleRefresh}
            isRefreshing={refreshing}
            variant="admin"
          />
        </div>
      </div>

      {error && (
        <div className="admin-error">
          <FontAwesomeIcon icon={faInfoCircle} />
          <span>{error}</span>
        </div>
      )}

      {/* KPI bar */}
      <div className="kpi-c-wrapper">
        <div className="kpi-stat-bar">
          <div className="kpi-stat">
            <span className="kpi-stat-value">{kpis.total}</span>
            <span className="kpi-stat-label">{t("Total")}</span>
          </div>
          <div className="kpi-stat">
            <span className="kpi-stat-value" style={{ color: "#66bb6a" }}>{kpis.active}</span>
            <span className="kpi-stat-label">{t("Active")}</span>
          </div>
          <div className="kpi-stat">
            <span className="kpi-stat-value" style={{ color: "#9e9e9e" }}>{kpis.paused}</span>
            <span className="kpi-stat-label">{t("Paused")}</span>
          </div>
          <div className="kpi-stat">
            <span className="kpi-stat-value" style={{ color: "#ef5350" }}>{kpis.revoked}</span>
            <span className="kpi-stat-label">{t("Revoked")}</span>
          </div>
          <div className="kpi-stat">
            <span className="kpi-stat-value" style={{ color: "#ffa726" }}>{kpis.pending}</span>
            <span className="kpi-stat-label">{t("Pending")}</span>
          </div>
          <div className="kpi-stat">
            <span className="kpi-stat-value">{kpis.uniqueOrgs}</span>
            <span className="kpi-stat-label">{t("Orgs")}</span>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="admin-toolbar">
        <div className="admin-toolbar-left">
          <div className="admin-search-wrap">
            <FontAwesomeIcon icon={faSearch} className="admin-search-icon" />
            <input
              type="text"
              className="admin-search-input"
              placeholder={t("Search users, orgs, keys...")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
        <div className="admin-toolbar-right">
          <select
            className="admin-filter-select"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="all">{t("All Statuses")}</option>
            <option value="active">{t("Active")}</option>
            <option value="paused">{t("Paused")}</option>
            <option value="revoked">{t("Revoked")}</option>
            <option value="pending">{t("Pending")}</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className={`admin-table-container ${refreshing ? "shimmer-overlay" : ""}`}>
        {pagedRows.length > 0 ? (
          <table className="admin-table partnerships-admin-table">
            <thead>
              <tr>
                <th className="sortable" onClick={() => handleSort("partner_key")}>
                  {t("Key")}
                  <Tooltip title={t("Unique partnership key shared between donor and receiver")} arrow placement="top">
                    <span className="th-info"><FontAwesomeIcon icon={faInfoCircle} /></span>
                  </Tooltip>
                  <SortIcon col="partner_key" />
                </th>
                <th className="sortable" onClick={() => handleSort("donor_username")}>
                  {t("Donor")}
                  <Tooltip title={t("The user and organization providing materials")} arrow placement="top">
                    <span className="th-info"><FontAwesomeIcon icon={faInfoCircle} /></span>
                  </Tooltip>
                  <SortIcon col="donor_username" />
                </th>
                <th className="sortable" onClick={() => handleSort("receiver_username")}>
                  {t("Receiver")}
                  <Tooltip title={t("The user and organization receiving materials")} arrow placement="top">
                    <span className="th-info"><FontAwesomeIcon icon={faInfoCircle} /></span>
                  </Tooltip>
                  <SortIcon col="receiver_username" />
                </th>
                <th className="sortable" onClick={() => handleSort("status")}>
                  {t("Status")}
                  <Tooltip title={t("Active = auto-requesting · Paused = temporarily stopped · Revoked = permanently ended")} arrow placement="top">
                    <span className="th-info"><FontAwesomeIcon icon={faInfoCircle} /></span>
                  </Tooltip>
                  <SortIcon col="status" />
                </th>
                <th className="sortable" onClick={() => handleSort("max_listing_pct")}>
                  {t("Max Alloc.")}
                  <Tooltip title={t("Maximum percentage of each listing the donor allocates to this partner via auto-request")} arrow placement="top">
                    <span className="th-info"><FontAwesomeIcon icon={faInfoCircle} /></span>
                  </Tooltip>
                  <SortIcon col="max_listing_pct" />
                </th>
                <th className="sortable" onClick={() => handleSort("created_at")}>
                  {t("Created")}
                  <Tooltip title={t("When the partnership key was generated")} arrow placement="top">
                    <span className="th-info"><FontAwesomeIcon icon={faInfoCircle} /></span>
                  </Tooltip>
                  <SortIcon col="created_at" />
                </th>
                <th className="sortable" onClick={() => handleSort("claimed_at")}>
                  {t("Claimed")}
                  <Tooltip title={t("When the receiver claimed the key and activated the partnership")} arrow placement="top">
                    <span className="th-info"><FontAwesomeIcon icon={faInfoCircle} /></span>
                  </Tooltip>
                  <SortIcon col="claimed_at" />
                </th>
                <th className="th-actions">
                  {t("Actions")}
                  <Tooltip title={t("Pause, resume, revoke, or restore partnerships")} arrow placement="top">
                    <span className="th-info"><FontAwesomeIcon icon={faInfoCircle} /></span>
                  </Tooltip>
                </th>
              </tr>
            </thead>
            <tbody>
              {pagedRows.map((p) => (
                <tr key={p.id}>
                  <td>
                    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      <span className="admin-cell-mono" style={{ fontSize: "0.72rem" }}>
                        {p.partner_key ? `${p.partner_key.slice(0, 12)}...` : "—"}
                      </span>
                      {p.partner_key && (
                        <span onClick={(e) => e.stopPropagation()}>
                          <ActionButton variant="default" iconOnly icon={faCopy}
                            tooltip={t("Copy")}
                            onClick={() => handleCopyKey(p.partner_key)}
                            flashOnClick />
                        </span>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="admin-cell-stack">
                      <span className="admin-cell-primary">{p.donor_username || "—"}</span>
                      <span className="admin-cell-secondary">{p.donor_org_name || ""}</span>
                    </div>
                  </td>
                  <td>
                    <div className="admin-cell-stack">
                      <span className="admin-cell-primary">{p.receiver_username || "—"}</span>
                      <span className="admin-cell-secondary">{p.receiver_org_name || ""}</span>
                    </div>
                  </td>
                  <td><StatusBadge status={p.status} /></td>
                  <td>
                    <span className="admin-cell-mono">{p.max_listing_pct ?? "—"}%</span>
                  </td>
                  <td><FormattedDate date={p.created_at} /></td>
                  <td><FormattedDate date={p.claimed_at} /></td>
                  <td className="td-actions">
                    <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                    {p.status === "active" && (
                      <>
                        <Tooltip title={t("Pause")} arrow>
                          <span>
                            <ActionButton variant="secondary" iconOnly icon={faPause}
                              onClick={() => openActionModal(p.id, "pause", `${p.donor_username || ""} → ${p.receiver_username || ""}`)} />
                          </span>
                        </Tooltip>
                        <Tooltip title={t("Revoke")} arrow>
                          <span>
                            <ActionButton variant="danger" iconOnly icon={faBan}
                              onClick={() => openActionModal(p.id, "revoke", `${p.donor_username || ""} → ${p.receiver_username || ""}`)} />
                          </span>
                        </Tooltip>
                      </>
                    )}
                    {p.status === "paused" && (
                      <>
                        <Tooltip title={t("Resume")} arrow>
                          <span>
                            <ActionButton variant="secondary" iconOnly icon={faPlay}
                              onClick={() => openActionModal(p.id, "resume", `${p.donor_username || ""} → ${p.receiver_username || ""}`)} />
                          </span>
                        </Tooltip>
                        <Tooltip title={t("Revoke")} arrow>
                          <span>
                            <ActionButton variant="danger" iconOnly icon={faBan}
                              onClick={() => openActionModal(p.id, "revoke", `${p.donor_username || ""} → ${p.receiver_username || ""}`)} />
                          </span>
                        </Tooltip>
                      </>
                    )}
                    {p.status === "revoked" && (
                      <Tooltip title={t("Restore")} arrow>
                        <span>
                          <ActionButton variant="secondary" iconOnly icon={faRotateLeft}
                            onClick={() => openActionModal(p.id, "restore", `${p.donor_username || ""} → ${p.receiver_username || ""}`)} />
                        </span>
                      </Tooltip>
                    )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="admin-empty-state">
            <FontAwesomeIcon icon={faHandshake} />
            <p>
              {debouncedSearch
                ? t("No partnerships matching") + ` "${debouncedSearch}"`
                : filterStatus !== "all"
                  ? t("No partnerships with status") + ` "${filterStatus}"`
                  : t("No partnerships created yet")}
            </p>
          </div>
        )}
      </div>

      {/* Pagination */}
      <div className="admin-table-footer">
        <span className="table-count">
          {t("Showing")} {pagedRows.length} {t("of")} {filteredRows.length}
        </span>
        {totalPages > 1 && (
          <div className="activity-pagination">
            <button
              className="activity-page-btn"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            <span className="activity-page-info">
              {page} / {totalPages}
            </span>
            <button
              className="activity-page-btn"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>
        )}
      </div>

      <PartnershipActionModal
        open={actionModal.open}
        onClose={() => setActionModal({ open: false, action: null, id: null, partnerName: "" })}
        onConfirm={handleActionConfirm}
        action={actionModal.action || "pause"}
        partnerName={actionModal.partnerName}
        darkMode={darkMode}
        isProcessing={actionProcessing}
      />
    </div>
  );
}

export default PartnershipsSection;
